import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { TranslateService } from '@ngx-translate/core'
import { catchError, exhaustMap, filter, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { UserAccountsApiService } from './user-accounts-api.service'
import * as UserAccountsActions from './user-accounts.actions'
import { UserAccountsFacade } from './user-accounts.facade'
import { UserAccountsEntity } from './user-accounts.models'
import { GbPaginatedResponse } from '@galabid/gb-dashboard/core/data-access'
import { GbDashboardDialogService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-dashboard-dialog-service/gb-dashboard-dialog.service'
import { GbArchiveUserDialogComponent } from 'libs/gb-dashboard/staff/feature-shell/src/lib/gb-archive-user-dialog/gb-archive-user-dialog.component'

@Injectable()
export class UserAccountsEffects {
  private actions$ = inject(Actions)

  loadUserAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountsActions.loadUserAccounts),
      withLatestFrom(this.userAccountsFacade.allUserAccounts$),
      switchMap(([action, allUserAccounts]) => {
        const offset = action.shouldReset ? 0 : allUserAccounts.length || 0

        return this.userAccountsApiService
          .loadUserAccounts(offset, action.searchTerm, action.searchBy, action.ordering)
          .pipe(
            map((response: GbPaginatedResponse<UserAccountsEntity>) => {
              if (!action.shouldReset) {
                return UserAccountsActions.loadUserAccountsSuccess({
                  userAccounts: response.results,
                  count: response.count,
                })
              } else {
                return UserAccountsActions.setUserAccounts({ userAccounts: response.results, count: response.count })
              }
            }),
            catchError((error) => {
              return of(UserAccountsActions.loadUserAccountsFailure({ error }))
            }),
          )
      }),
    ),
  )

  openArchiveUserDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountsActions.openArchiveUserDialog),
      exhaustMap(() =>
        this.gbDashboardDialogService.getDialogObs(GbArchiveUserDialogComponent, {
          width: '500px',
        }),
      ),
      filter((res) => !!res),
      map(() => UserAccountsActions.archiveUser()),
    ),
  )

  archiveUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountsActions.archiveUser),
      withLatestFrom(this.userAccountsFacade.selectedUserAccount$),
      switchMap(([, selectedUserAccount]) =>
        this.userAccountsApiService.archiveUser(selectedUserAccount?.uuid).pipe(
          map((response: UserAccountsEntity) => UserAccountsActions.archiveUserSuccess({ userAccount: response })),
          catchError((error: HttpErrorResponse) => of(UserAccountsActions.archiveUserFailure({ error }))),
        ),
      ),
    ),
  )

  archiveUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccountsActions.archiveUserSuccess),
        tap(() => {
          this.gbDashboardDialogService.closeDialog(GbArchiveUserDialogComponent)
          this.gbNotificationService.success('USER_ACCOUNTS.ARCHIVE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  removeFromUnsubscribeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountsActions.removeFromUnsubscribeList),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'USER_ACCOUNTS.REMOVE_FROM_UNSUBSCRIBE_LIST_CONFIRM_TITLE',
            body: this.translateService.instant('USER_ACCOUNTS.REMOVE_FROM_UNSUBSCRIBE_LIST_CONFIRM_DESC', {
              email: action.email,
            }),
            confirmButtonText: 'USER_ACCOUNTS.REMOVE_FROM_UNSUBSCRIBE_LIST_CONFIRM_OK',
          },
          action,
        ),
      ),
      tap((action) => this.gbLoaderService.showLoaderDialog(action.type)),
      switchMap((action) =>
        this.userAccountsApiService.removeFromUnsubscribeList(action.uuid).pipe(
          map(() => UserAccountsActions.removeFromUnsubscribeListSuccess({ uuid: action.uuid })),
          catchError((error: HttpErrorResponse) => of(UserAccountsActions.removeFromUnsubscribeListFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(action.type)),
        ),
      ),
    ),
  )

  removeFromUnsubscribeListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccountsActions.removeFromUnsubscribeListSuccess),
        tap(() => {
          this.gbNotificationService.success('USER_ACCOUNTS.REMOVE_FROM_UNSUBSCRIBE_LIST_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          UserAccountsActions.loadUserAccountsFailure,
          UserAccountsActions.archiveUserFailure,
          UserAccountsActions.removeFromUnsubscribeListFailure,
        ),
        map((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private userAccountsFacade: UserAccountsFacade,
    private userAccountsApiService: UserAccountsApiService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private translateService: TranslateService,
    private gbLoaderService: GbLoaderService,
  ) {}
}
